import React, { useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import {
  CardCvcElement,
  CardElement,
  CardExpiryElement,
  CardNumberElement,
  Elements,
  useElements,
  useStripe,
} from '@stripe/react-stripe-js';
import { loadStripe } from '@stripe/stripe-js';
import clientInfo from 'common/lib/clientInfo';
import omit from 'lodash/omit';
import isEmpty from 'lodash/isEmpty';

import { StripeFormContext } from './StripeFormContext';

// const ZIP_CODE_REGEX = new RegExp(/(^\d{5}$)|(^\d{9}$)|(^\d{5}-\d{4}$)/);
const STRIPE_FIELDS = [
  { errorKey: 'cardNumber', element: CardNumberElement, text: 'number' },
  {
    errorKey: 'cardExpiry',
    element: CardExpiryElement,
    text: 'expiration date',
  },
  { errorKey: 'cardCvc', element: CardCvcElement, text: 'security card' },
  { errorKey: 'card', element: CardElement, text: 'info' },
];

const Provider = ({ children, skipBillingDetailsValidation }) => {
  const stripe = useStripe();
  const elements = useElements();

  const [billingDetails, setBillingDetails] = useState({});
  const [cardErrors, setCardErrors] = useState({});
  const [paymentMethodId, setPaymentMethodId] = useState();

  const nameError = () =>
    !skipBillingDetailsValidation && isEmpty(billingDetails.name)
      ? {
          name: 'The name on your card must be provided.',
        }
      : {};

  const zipError = () =>
    !skipBillingDetailsValidation &&
    (!billingDetails.zip_postal_code || billingDetails.zip_postal_code === '')
      ? {
          zip_postal_code: "Your card's zip must be valid.",
        }
      : {};

  const validateBillingDetail = ({ target: { name } }) => {
    const fn = name === 'name' ? nameError : zipError;
    setCardErrors((previous) => ({
      ...previous,
      ...fn(),
    }));
  };

  const findIncompletedFields = () => {
    const errors = {
      ...nameError(),
      ...zipError(),
    };
    STRIPE_FIELDS.forEach(({ errorKey, element, text }) => {
      const field = elements.getElement(element);
      if (field?._invalid) {
        errors[errorKey] = `Your card's ${text} is invalid.`;
      }
      if (field?._empty) {
        errors[errorKey] = `Your card's ${text} must be provided.`;
      }
    });

    return errors;
  };

  const createPaymentMethod = async (force = false) => {
    // eslint-disable-next-line no-undef
    if (window.kjbStripeElementSkip && CAN_SKIP_STRIPE) {
      return Promise.resolve({
        paymentMethod: { id: window.kjbStripePaymentMethodId },
      });
    }

    if (paymentMethodId && !force) {
      return Promise.resolve({ paymentMethod: { id: paymentMethodId } });
    }

    setCardErrors((previous) => ({
      ...omit(previous, 'general'),
    }));

    const incompletedFields = findIncompletedFields();
    if (!isEmpty(incompletedFields)) {
      setCardErrors(incompletedFields);
      return Promise.resolve({
        error: { message: 'All fields must be valid' },
      });
    }

    const card = elements.getElement(CardNumberElement) || elements.getElement(CardElement);

    const response = await stripe.createPaymentMethod({
      type: 'card',
      card,
      billing_details: {
        name: billingDetails.name,
        address: {
          postal_code: billingDetails.zip_postal_code,
          country: billingDetails.country,
        },
      },
    });

    if (window.AnalyticsBase) {
      window.AnalyticsBase.trackEvent('Payment Submitted');
    }

    if (response.error) {
      if (window.AnalyticsBase) {
        window.AnalyticsBase.trackEvent('Payment Error');
      }
      setCardErrors((previous) => ({
        ...previous,
        general: response.error.message,
      }));
    }

    if (window.AnalyticsBase) {
      window.AnalyticsBase.trackEvent('Payment Success');
    }

    return response; // { error, paymentMethod }
  };

  const handleBillingDetailsChange = ({ target: { name, value } }) => {
    if (cardErrors[name]) {
      setCardErrors((prev) => ({
        ...omit(prev, name),
      }));
    }

    setBillingDetails((previous) => ({
      ...previous,
      [name]: value,
    }));
  };

  const handleCardChange = async ({ elementType, error, complete }) => {
    setCardErrors((prev) => ({
      ...omit(prev, 'general'),
      [elementType]: error ? error.message : undefined,
    }));
    if (elementType === 'card') {
      if (complete) {
        const { paymentMethod: { id, billing_details: { address } = {}, card } = {} } =
          await createPaymentMethod(true);
        setPaymentMethodId(id);
        setBillingDetails((previous) => ({
          ...previous,
          postal_code: address?.postal_code,
          country: card?.country,
        }));
      } else {
        setPaymentMethodId();
      }
    }
  };

  const contextValue = {
    billingDetails,
    cardErrors,
    createPaymentMethod,
    elements,
    handleBillingDetailsChange,
    handleCardChange,
    setCardErrors,
    stripe,
    validateBillingDetail,
  };

  return <StripeFormContext.Provider value={contextValue}>{children}</StripeFormContext.Provider>;
};

Provider.propTypes = {
  children: PropTypes.node.isRequired,
  skipBillingDetailsValidation: PropTypes.bool.isRequired,
};

export const StripeFormProvider = ({ amount, children, skipBillingDetailsValidation }) => {
  const stripePromise = useMemo(
    () => loadStripe(clientInfo.get('stripe_config')['data-stripe-elements-form']),
    [],
  );

  const options = useMemo(() => {
    if (amount) {
      return {
        amount,
        appearance: {
          variables: {
            borderRadius: '0.5rem',
          },
        },
        currency: 'usd',
        mode: 'subscription',
        paymentMethodTypes: ['card'],
        paymentMethodCreation: 'manual',
      };
    }
    return undefined;
  }, [amount]);

  return (
    <Elements options={options} stripe={stripePromise}>
      <Provider skipBillingDetailsValidation={skipBillingDetailsValidation}>{children}</Provider>
    </Elements>
  );
};

StripeFormProvider.defaultProps = {
  amount: undefined,
  skipBillingDetailsValidation: false,
};

StripeFormProvider.propTypes = {
  amount: PropTypes.number,
  children: PropTypes.node.isRequired,
  skipBillingDetailsValidation: PropTypes.bool,
};
